import React, { useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import LinkText from '../components/LinkText';

const TYPE_META = {
  service: {
    label: 'Service',
    badge: 'bg-blue-100 text-blue-800 border-blue-300',
  },
  inspection: {
    label: 'Inspection',
    badge: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  },
  wip: {
    label: 'WIP',
    badge: 'bg-purple-100 text-purple-800 border-purple-300',
  },
  operations: {
    label: 'Operations',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  },
};

function formatTime(t) {
  if (!t) return '—';

  const [hh, mm = '00'] = t.split(':');
  const h = parseInt(hh || '0', 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;

  return `${h12}:${mm.padStart(2, '0')} ${ampm}`;
}

function formatDate(ymd) {
  if (!ymd) return '—';

  const match = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) {
    return ymd;
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );

  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const TechScheduleDetails = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();
  const location = useLocation();

  const { type, id } = useParams();
  const [searchParams] = useSearchParams();

  const selectedDate = searchParams.get('date') || '';

  const storageKey = `techScheduleDetails:${type}:${id}:${selectedDate}`;

  const storedData = useMemo(() => {
    try {
      const value = sessionStorage.getItem(storageKey);

      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error('Unable to restore schedule details', error);
      return null;
    }
  }, [storageKey]);

  const schedule =
    location.state?.schedule ||
    storedData?.schedule ||
    null;

  const userFName =
    location.state?.userFName ||
    storedData?.userFName ||
    null;

  const [actionLoading, setActionLoading] = useState(false);

  if (!schedule) {
    return (
      <div className="mx-auto max-w-[1200px] p-4">
        <div className="rounded-xl border bg-white p-8 shadow-sm">
          <h1 className="text-xl font-semibold text-gray-900">
            Schedule Details
          </h1>

          <p className="mt-3 text-gray-500">
            The schedule information is no longer available.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-5 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white"
          >
            Back to Schedules
          </button>
        </div>
      </div>
    );
  }

  const typeMeta =
    TYPE_META[schedule._type] ||
    {
      label: schedule._type,
      badge: 'bg-gray-100 text-gray-700 border-gray-300',
    };

  const isCanceled =
    (schedule.status || '').toLowerCase().includes('cancel');

  const isComplete =
    (schedule.status || '').toLowerCase().includes('complete');

  const leadTech =
    schedule._type === 'service'
      ? schedule.serv_tech_name
      : schedule.tech_name || schedule.meta?.tech_name;

  const getAdditionalTechNames = () => {
    if (schedule._type === 'service') {
      return Array.isArray(schedule.additional_tech)
        ? schedule.additional_tech
            .map((tech) => tech.tech_name)
            .filter(Boolean)
        : [];
    }

    if (schedule._type === 'inspection') {
      return Array.isArray(schedule.additional_tech)
        ? schedule.additional_tech
            .map((tech) => tech.name)
            .filter(Boolean)
        : [];
    }

    if (schedule._type === 'wip') {
      if (!schedule.additional_tech) return [];

      if (Array.isArray(schedule.additional_tech)) {
        return schedule.additional_tech
          .map((tech) =>
            typeof tech === 'string'
              ? tech
              : tech.name || tech.tech_name
          )
          .filter(Boolean);
      }

      return String(schedule.additional_tech)
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean);
    }

    return [];
  };

  const additionalTechs = getAdditionalTechNames();

  /*
   * Important:
   *
   * WIP:
   *   schedule.notes = job description
   *   meta.coordinator_comment = internal/coordinator notes
   *
   * Service:
   *   TROUBLE = service ticket description
   *   notes = internal notes
   *
   * Inspection:
   *   notes = inspection notes
   */
  const description =
    schedule._type === 'service'
      ? schedule.TROUBLE
      : schedule._type === 'wip'
      ? schedule.notes
      : schedule._type === 'operations'
      ? schedule.notes
      : '';

  const internalNotes =
    schedule._type === 'wip' ||
    schedule._type === 'operations'
      ? schedule.meta?.coordinator_comment
      : schedule.notes;

  const handleServiceActivation = async () => {
    const techName = schedule.meta?.tech;

    if (
      userFName &&
      techName &&
      userFName.toLowerCase() !== techName.toLowerCase()
    ) {
      toast.info(
        'You are not authorized to activate /or see details of this call.',
        { position: 'top-center' }
      );
      return;
    }

    if ((schedule.status || '').toLowerCase() === 'activated') {
      navigate(`/t/tservdetail/${schedule.id}`);
      return;
    }

    setActionLoading(true);

    try {
      const response = await axios.post(
        `${apiUrl}/service_crud.php`,
        {
          status: 'activateCall',
          id: schedule.id,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data?.[0] === 'success') {
        navigate(`/t/tservdetail/${schedule.id}`);
      } else {
        toast.info('Failed to activate call.', {
          position: 'top-center',
        });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        toast.error(
          "Oops!! You don't have access to this area. Speak to the CFO.",
          { position: 'top-center' }
        );
      } else {
        toast.error('Unable to activate service call.', {
          position: 'top-center',
        });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrimaryAction = () => {
    switch (schedule._type) {
      case 'service':
        handleServiceActivation();
        break;

      case 'inspection':
        navigate(`/t/tinspdetail/${schedule.id}`);
        break;

      case 'wip':
        navigate(`/t/twipdetails/${schedule.id}`);
        break;

      default:
        break;
    }
  };

  const getActionLabel = () => {
    if (schedule._type === 'service') {
      return (schedule.status || '').toLowerCase() === 'activated'
        ? 'Open Service Call'
        : 'Activate & Open Service Call';
    }

    if (schedule._type === 'inspection') {
      return 'Open Inspection';
    }

    if (schedule._type === 'wip') {
      return 'Open WIP Details';
    }

    return null;
  };

  const actionLabel = getActionLabel();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-[1200px] px-4 py-6">

        {/* BACK */}
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900"
        >
          ← Back to Schedules
        </button>

        {/* HEADER */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">

            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">

                <span
                  className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${typeMeta.badge}`}
                >
                  {typeMeta.label}
                </span>

                <span
                  className={[
                    'inline-flex rounded-full px-3 py-1 text-xs font-semibold',
                    isComplete
                      ? 'bg-green-100 text-green-700'
                      : isCanceled
                      ? 'bg-red-100 text-red-700'
                      : 'bg-indigo-100 text-indigo-700',
                  ].join(' ')}
                >
                  {schedule.status || 'No Status'}
                </span>

              </div>

              <h1 className="text-2xl font-bold text-gray-900">
                {schedule.job_site || 'No Address'}
              </h1>

              {schedule.customer && (
                <p className="mt-1 text-lg text-gray-500">
                  {schedule.customer}
                </p>
              )}

              <p className="mt-2 text-xs text-gray-400">
                Schedule ID: {schedule.id}
              </p>
            </div>

            {/* PRIMARY ACTION */}
            {actionLabel && !isCanceled && (
              <button
                type="button"
                disabled={actionLoading}
                onClick={handlePrimaryAction}
                className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Please wait…' : actionLabel}
              </button>
            )}

          </div>
        </div>

        {/* CONTENT */}
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">

          {/* MAIN COLUMN */}
          <div className="space-y-5 lg:col-span-2">

            {/* SERVICE TROUBLE / WIP DESCRIPTION */}
            {description && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {schedule._type === 'service'
                    ? 'PO# / Service Ticket Notes'
                    : schedule._type === 'wip'
                    ? 'Job Description'
                    : 'Description'}
                </h2>

                <div
                  className={
                    schedule._type === 'service'
                      ? 'whitespace-pre-wrap text-base font-medium text-red-700'
                      : 'whitespace-pre-wrap text-base text-gray-900'
                  }
                >
                  <LinkText text={description} />
                </div>

              </section>
            )}

            {/* INTERNAL NOTES */}
            {internalNotes && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Internal Notes
                </h2>

                <div className="whitespace-pre-wrap text-gray-900">
                  <LinkText text={internalNotes} />
                </div>

              </section>
            )}

            {/* RELATED LINKS */}
            {Array.isArray(schedule.links) &&
              schedule.links.length > 0 && (
                <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                  <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Related Links
                  </h2>

                  <div className="space-y-3">
                    {schedule.links.map((link) => (
                      <div
                        key={link.id}
                        className="rounded-lg border border-gray-200 p-3"
                      >
                        <div className="text-sm font-semibold text-gray-900">
                          {link.label || 'Untitled'}
                        </div>

                        <a
                          href={
                            /^https?:\/\//i.test(link.link)
                              ? link.link
                              : `https://${link.link}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 block break-all text-sm text-indigo-600 hover:underline"
                        >
                          {link.link}
                        </a>
                      </div>
                    ))}
                  </div>

                </section>
              )}

          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-5">

            {/* SCHEDULE */}
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

              <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Schedule
              </h2>

              <div className="space-y-4">

                <div>
                  <div className="text-xs text-gray-400">
                    Selected Date
                  </div>

                  <div className="font-semibold text-gray-900">
                    {formatDate(schedule._date)}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-400">
                    Time
                  </div>

                  <div className="font-semibold text-gray-900">
                    {formatTime(schedule.start_time)}

                    {schedule.end_time
                      ? ` – ${formatTime(schedule.end_time)}`
                      : ''}
                  </div>
                </div>

                {schedule.meta?.schedule_from &&
                  schedule.meta?.schedule_to && (
                    <div>
                      <div className="text-xs text-gray-400">
                        Schedule Range
                      </div>

                      <div className="font-medium text-gray-900">
                        {formatDate(schedule.meta.schedule_from)}
                      </div>

                      <div className="text-xs text-gray-400">
                        through
                      </div>

                      <div className="font-medium text-gray-900">
                        {formatDate(schedule.meta.schedule_to)}
                      </div>
                    </div>
                  )}

              </div>
            </section>

            {/* TECHNICIANS */}
            {(leadTech || additionalTechs.length > 0) && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Technicians
                </h2>

                {leadTech && (
                  <div>
                    <div className="text-xs text-gray-400">
                      Lead Technician
                    </div>

                    <div className="font-semibold text-gray-900">
                      {leadTech}
                    </div>
                  </div>
                )}

                {additionalTechs.length > 0 && (
                  <div className="mt-4">

                    <div className="mb-1 text-xs text-gray-400">
                      Additional Technician(s)
                    </div>

                    {additionalTechs.map((tech, index) => (
                      <div
                        key={`${tech}-${index}`}
                        className="font-medium text-blue-700"
                      >
                        {tech}
                      </div>
                    ))}

                  </div>
                )}

              </section>
            )}

            {schedule.logged_by && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Logged By
                </h2>

                <div className="font-semibold text-gray-900">
                  {schedule.logged_by}
                </div>
              </section>
            )}
            
            {/* SERVICE CONTACT */}
            {schedule._type === 'service' && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Contact
                </h2>

                <div className="space-y-3">

                  <div>
                    <div className="text-xs text-gray-400">
                      Called By
                    </div>

                    <div className="font-medium text-gray-900">
                      {schedule.meta?.called_by || '—'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-400">
                      Phone
                    </div>

                    {schedule.meta?.phone ? (
                      <a
                        href={`tel:${schedule.meta.phone}`}
                        className="font-medium text-blue-600"
                      >
                        {schedule.meta.phone}
                      </a>
                    ) : (
                      <div>—</div>
                    )}
                  </div>

                </div>

              </section>
            )}

            {/* INSPECTION CONTACT */}
            {schedule._type === 'inspection' && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Primary Contact
                </h2>

                <div className="space-y-3">

                  <div>
                    <div className="text-xs text-gray-400">
                      Name
                    </div>

                    <div className="font-medium">
                      {schedule.meta?.contact_name || '—'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-400">
                      Phone
                    </div>

                    {schedule.meta?.contact_phone ? (
                      <a
                        href={`tel:${schedule.meta.contact_phone}`}
                        className="font-medium text-blue-600"
                      >
                        {schedule.meta.contact_phone}
                      </a>
                    ) : (
                      '—'
                    )}
                  </div>

                  <div>
                    <div className="text-xs text-gray-400">
                      Email
                    </div>

                    {schedule.meta?.contact_email ? (
                      <a
                        href={`mailto:${schedule.meta.contact_email}`}
                        className="break-all font-medium text-blue-600"
                      >
                        {schedule.meta.contact_email}
                      </a>
                    ) : (
                      '—'
                    )}
                  </div>

                </div>

              </section>
            )}

            {/* WIP INFO */}
            {schedule._type === 'wip' && (
              <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                <h2 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Job Information
                </h2>

                <div className="space-y-3">

                  <div>
                    <div className="text-xs text-gray-400">
                      Job #
                    </div>

                    <div className="font-bold text-green-800">
                      {schedule.meta?.job_number || '—'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-400">
                      Type
                    </div>

                    <div className="font-medium">
                      {schedule.meta?.type || '—'}
                    </div>
                  </div>

                  {schedule.user && (
                    <div>
                      <div className="text-xs text-gray-400">
                        Logged By
                      </div>

                      <div className="font-medium">
                        {schedule.user}
                      </div>
                    </div>
                  )}

                </div>

              </section>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default TechScheduleDetails;